import QRCode from 'qrcode';
import { supabase } from '../lib/supabase';
import { labelTargetUrl } from '../lib/appUrl';
import { getCompanyName } from '../lib/tenant';
import { qrPixelWidth, getFormat } from '../lib/labelFormats';
import { buildLabelPdfBlob } from './labelPdf';

// #322, Session 75. Both Brother profiles declared height_mm: 25.4 - a full
// inch - since Session 45. The PT-E560BT at Dallas takes 24mm tape, confirmed
// by Colby at 74z7, and 24mm is the widest tape the machine accepts at all.
//
// Nothing had broken because Brother output is browser-rendered HTML that
// scales to fit. It matters now because the tool profile derives a layout from
// these numbers rather than letting the browser absorb the error.
//
// 24 is the TAPE width, not the printable width - the tape's own unprintable
// margins sit inside it and have never been measured on the machine. The tool
// layout below therefore keeps its QR clear of the edges by more than the
// margin can plausibly be. See ISSUES.md #322 for the measurement still owed.
// Session 78a. The page now has to match the MEDIA, and this table is where
// that number lives.
//
// The paragraph directly above already named the gap: "24 is the TAPE width,
// not the printable width - the tape's own unprintable margins sit inside it
// and have never been measured on the machine." That stayed harmless while
// Brother output was browser-rendered HTML the browser scaled to fit. The PDF
// path made the page size real, and then two things happened in the same test
// round: Scale moved from "Fit to paper" to "Default" (correct - fit-to-paper
// is a free variable that silently rescales), and the driver moved to 180x360
// dpi. With nothing rescaling it any more, a 1.00in page on a narrower band
// loses the difference, and the first text row - the item name - sits nearest
// that edge. Colby's next print came off the machine with the name missing.
//
// It costs more than a clipped line. A QR needs a quiet zone of white on all
// four sides; clip the label and the code's quiet zone goes with it. jsQR then
// refuses a code a phone's native camera still reads - which is exactly the
// split that was in front of us.
//
// So `printable_mm` is the tape's INK BAND, not the tape. MEASURED at Session
// 78a on the PT-E560BT at Colby's desk: 23/32 in, which is 18.26mm. The tape is
// 24mm, so the head gives up 5.7mm to unprintable margin - about 2.9mm a side,
// which is what Brother's own figures predict and is why this is believable
// rather than merely convenient.
//
// This closes the measurement #322 has been owing since Session 75, and it is
// the first of Session 78a's hardware checks to close for a real reason rather
// than by inference.
//
// The value stays 18, NOT 18.26, and that is deliberate. A tape measure reads
// to 1/32in - 0.79mm - so the measurement's own resolution is three times the
// 0.26mm that "improving" the constant would buy. Rounding down puts the page
// safely inside a band we know to within a thirty-second, and the cost of being
// under is a marginally smaller label while the cost of being over is a clipped
// QR quiet zone that a scanner silently refuses. Do not tighten this to the
// measured figure. If it ever needs to move, measure again with calipers first.
const LABEL_PROFILES = {
  pt_e560bt_2x1:  { id: 'pt_e560bt_2x1',  label: 'PT-E560BT',  desc: '2"×1" standard',  width_mm: 50.8, height_mm: 24, printable_mm: 18, available: true },
  // Session 75. Replaces pt_e560bt_1x1 ('1"×1" compact', Session 45). The gate's
  // ladder has no 1x1 rung - it was too cramped to carry model, serial and
  // station, and fitted only a QR with a truncated serial beneath it. 1.5" of
  // continuous tape costs nothing extra and buys all three lines.
  pt_e560bt_tool: { id: 'pt_e560bt_tool', label: 'PT-E560BT',  desc: '1.5"×1" tool',   width_mm: 38.1, height_mm: 24, printable_mm: 18, available: true },
  zebra_2x1:      { id: 'zebra_2x1',       label: 'Zebra',      desc: '2"×1" standard',  width_mm: 50.8, height_mm: 25.4, available: true },
  zebra_3x1:      { id: 'zebra_3x1',       label: 'Zebra',      desc: '3"×1" shelf',     width_mm: 76.2, height_mm: 25.4, available: false },
};

export { LABEL_PROFILES };

// #334, Session 77. THE translation between the two vocabularies, and the only
// one. Moved up here from beside the QR helpers, because it stopped being a
// private sizing detail the moment the receive path and LabelCard started
// speaking formats too.
//
// The two vocabularies are not redundant and neither is going away:
//
//   LABEL_PROFILES is a MACHINE plus a MEDIA. It carries millimetres, and ZPL
//   needs millimetres to compute dots. Four rows, because there are four
//   machine/media pairs Stede can address.
//
//   LABEL_FORMATS is a RECTANGLE. Seven rows, because there are seven sizes,
//   and it is what every user-facing surface built since Session 75 speaks -
//   the modal's chips, the scan distances, the destination rules.
//
// Before this, the receive path spoke only profiles and everything Session 75
// built spoke only formats, so a rung added to the ladder was invisible to
// receiving and the toggle's copy came from a different source of truth than
// the modal's. Nothing printed wrong; what it cost was future correctness.
const PROFILE_FORMAT = {
  pt_e560bt_2x1:  '2x1',
  pt_e560bt_tool: 'tool_1_5x1',
  zebra_2x1:      '2x1',
  zebra_3x1:      '3x1',
};

// Every printer option carries BOTH ids from here on: profileId for the machine
// that has to render it, formatId for anything a person reads. Derived, never
// hand-written twice - that duplication is exactly what #334 is about.
export function optionFormatId(option) {
  return PROFILE_FORMAT[option?.profileId] || '2x1';
}

// ---------------------------------------------------------------------------
// Zebra ZPL dispatch — Session 49 (8c)
// ---------------------------------------------------------------------------

// ^ and ~ are ZPL's own "this is an instruction" characters — if an item or
// tool name ever contained one, it would corrupt the label. Strip, don't
// escape (ZPL has no escape sequence for them inside ^FD text).
function zplEscape(str) {
  return String(str || '').replace(/[\^~]/g, '').trim();
}

function truncateForLabel(str, max) {
  const s = zplEscape(str);
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

// 203dpi is the standard ZD421 configuration — roughly 8 dots per mm. This
// derives the label's dot dimensions from the same width_mm/height_mm already
// used for Brother's print styling, so zebra_3x1 (once enabled) won't need
// new hardcoded numbers.
function mmToDots(mm) {
  return Math.round(mm * 8);
}

// Best-effort ZPL using Zebra's documented ^BQ (QR) command. Not yet tested
// on real hardware — no relay agent exists until 8d ships. Uses "-" instead
// of "·" since the built-in ZPL font's support for that character isn't
// guaranteed.
export function buildInventoryZpl(item, branchName, profile = LABEL_PROFILES.zebra_2x1) {
  const url = labelTargetUrl(`/inventory/scan/${item.id}`);
  const pw = mmToDots(profile.width_mm);
  const ll = mmToDots(profile.height_mm);
  const name = truncateForLabel(item?.name, 24);
  const sku = truncateForLabel(item?.sku, 20);
  const uom = truncateForLabel(item?.uom, 10);
  const branch = truncateForLabel(branchName, 20);

  return `^XA
^PW${pw}
^LL${ll}
^FO20,20^BQN,2,4
^FDQA,${url}^FS
^FO160,22^A0N,22,22^FD${name}^FS
^FO160,50^A0N,16,16^FD${sku} - ${uom}^FS
^FO160,72^A0N,14,14^FD${branch}^FS
^FO160,90^A0N,11,11^FD${truncateForLabel(getCompanyName(), 24)}^FS
^XZ`;
}

export function buildToolZpl(tool, branchName, profile = LABEL_PROFILES.zebra_2x1) {
  const url = labelTargetUrl(`/tool/${tool.id}`);
  const pw = mmToDots(profile.width_mm);
  const ll = mmToDots(profile.height_mm);
  const name = truncateForLabel(tool?.name, 24);
  const serial = truncateForLabel(tool?.serial_number, 20);
  const branch = truncateForLabel(branchName, 20);

  return `^XA
^PW${pw}
^LL${ll}
^FO20,20^BQN,2,4
^FDQA,${url}^FS
^FO160,22^A0N,22,22^FD${name}^FS
^FO160,50^A0N,16,16^FDS/N: ${serial}^FS
^FO160,72^A0N,14,14^FD${branch}^FS
^XZ`;
}

// Active Zebra printers registered at a given branch. Filtered client-side
// by branch name to match the branchName string LabelCard/InventoryLabelPrint
// already carry, same pattern as the rest of the app's branch filtering.
export async function fetchActivePrinters(branchName) {
  // Session 51 fix — branches!branch_id disambiguates the embed now that
  // branches.receive_default_printer_id creates a second FK path between
  // these two tables. Without the hint, PostgREST can't tell which
  // relationship to embed through and errors with PGRST201.
  const { data, error } = await supabase
    .from('printer_registry')
    .select('*, branches!branch_id(name)')
    .eq('is_active', true)
    .order('name');
  if (error) {
    console.error('fetchActivePrinters failed:', error);
    return [];
  }
  return (data || []).filter(p => p.branches?.name === branchName);
}

// Which Zebra printers are actively registered at this branch, and whether the
// branch has an explicit receive-flow default set (receive_default_printer_id on
// branches, Session 51 — written by PrinterDetailPanel's "Set as receive
// default" control, Session 77). receiveDefaultPrinter comes back null if the
// branch never configured one, or if the printer it pointed to has since been
// deactivated — either way callers fall back to standard priority order, no
// separate query needed since zebraPrinters already has the full active list to
// check against.
//
// #337, Session 77. `has_brother_printer` is no longer read here, or anywhere.
// It was a hand-flipped boolean meaning "this branch owns a Brother", and it was
// being used to answer a question it cannot answer: whether the person at this
// browser can print right now. It gated wrong in both directions — unreachable
// at a branch whose flag was never set even with the printer on USB, and offered
// from anywhere on earth at a branch whose flag was set. Session 76 removed the
// same gate from the batch modal; this is the rest of the class.
//
// The column still exists and is still true about Dallas's equipment list. It is
// simply not evidence about reachability, so nothing asks it any more.
export async function fetchBranchPrinterOptions(branchName) {
  const [{ data: branchData, error: branchError }, zebraPrinters] = await Promise.all([
    supabase.from('branches').select('receive_default_printer_id').eq('name', branchName).maybeSingle(),
    fetchActivePrinters(branchName),
  ]);

  if (branchError) console.error('fetchBranchPrinterOptions (branches) failed:', branchError);

  const receiveDefaultPrinter = branchData?.receive_default_printer_id
    ? zebraPrinters.find(p => p.id === branchData.receive_default_printer_id) || null
    : null;

  return {
    zebraPrinters,
    receiveDefaultPrinter,
  };
}

// Extracted from LabelCard.js's inline option-building useEffect — same
// ordering rules (tools: local first, inventory: Zebra first), shared so the
// Batch Print Modal doesn't grow its own copy that can drift from LabelCard's.
//
// #337, Session 77. Two changes, and both are the batch modal's Session 76
// reasoning arriving in the surfaces that session did not open:
//
//   `brother` became `local`. The mechanism is the browser's own print dialog,
//   so the job goes wherever the OS is pointed and Stede never learns where.
//   Naming a specific machine was a claim it cannot keep. Name the mechanism.
//
//   The hasBrother gate is gone. Every machine running a browser has a print
//   dialog, so this destination is always reachable — what it cannot do is
//   promise which printer is on the other end.
//
// #334: every option now carries formatId alongside profileId, so a caller that
// wants to SHOW something reads the format and a caller that has to RENDER
// something reads the profile. Neither has to know about the other's table.
export function buildPrinterOptionList({ zebraPrinters, mode }) {
  const isToolMode = mode === 'tool';
  const withFormat = opt => ({ ...opt, formatId: optionFormatId(opt) });

  const zebraOptions = zebraPrinters.map(printer => withFormat({
    type: 'zebra',
    profileId: 'zebra_2x1',
    printer,
    key: `zebra_${printer.id}`,
  }));
  const localOptions = (isToolMode
    ? [
        { type: 'local', profileId: 'pt_e560bt_tool', key: 'local_tool' },
        { type: 'local', profileId: 'pt_e560bt_2x1', key: 'local_2x1' },
      ]
    : [{ type: 'local', profileId: 'pt_e560bt_2x1', key: 'local_2x1' }]
  ).map(withFormat);

  return isToolMode
    ? [...localOptions, ...zebraOptions]
    : [...zebraOptions, ...localOptions];
}

// Bypass #4 — resolves the printer to use automatically, with no picker
// shown, for the receive-flow's silent auto-print. Priority: (1) the
// branch's explicit receive_default_printer_id if set and still active,
// (2) the mode's standard priority order, (3) null if nothing at all is
// configured at that branch. Used both to display accurate copy on
// ReceiveSheetContent's toggle and to actually dispatch on confirm.
export async function resolveAutoPrintOption(branchName, mode = 'inventory') {
  const { zebraPrinters, receiveDefaultPrinter } = await fetchBranchPrinterOptions(branchName);

  if (receiveDefaultPrinter) {
    return {
      type: 'zebra',
      profileId: 'zebra_2x1',
      formatId: '2x1',
      printer: receiveDefaultPrinter,
      key: `zebra_${receiveDefaultPrinter.id}`,
    };
  }

  // #337, Session 77. This can no longer return null in practice, because the
  // local destination is always in the list. That is a real behaviour change and
  // it is the intended one: a branch with no Zebra used to get "Printing
  // unavailable" on the receive toggle, which was false — the receiver had a
  // print dialog the whole time. The null branch stays for the impossible case.
  const options = buildPrinterOptionList({ zebraPrinters, mode });
  return options[0] || null;
}

// Batch entry point for Swaps 2 & 3. branchNames is the list of branch names
// carried by whatever's selected — FleetPage's admin/exec "Select all" can
// span branches, InventoryLabelPrint never can since it's already scoped to
// one branchId. A Zebra printer lives on one branch's LAN, so a selection
// spanning branches drops to Brother only (which prints from this computer
// and doesn't care what branch the records say) rather than inventing
// per-branch job splitting nobody asked for.
//
// #337: "Brother" in the comment above is now the `local` destination - this
// computer's print dialog, which likewise does not care what branch the records
// say. The reasoning is unchanged; only the honest name for it is.
export async function resolveBatchPrinterOptions(branchNames, mode) {
  const uniqueBranches = [...new Set(branchNames.filter(Boolean))];

  if (uniqueBranches.length === 0) {
    return { options: [], crossBranch: false, branchNames: [] };
  }

  if (uniqueBranches.length > 1) {
    const options = [{ type: 'local', profileId: 'pt_e560bt_2x1', formatId: '2x1', key: 'local_2x1' }];
    return { options, crossBranch: true, branchNames: uniqueBranches };
  }

  const { zebraPrinters } = await fetchBranchPrinterOptions(uniqueBranches[0]);
  const options = buildPrinterOptionList({ zebraPrinters, mode });
  return { options, crossBranch: false, branchNames: uniqueBranches };
}

// Creates the real print_jobs row. branch_id comes from the printer itself,
// since printer_registry.branch_id is the source of truth once a printer
// is chosen.
export async function dispatchZebraPrint({ printer, userId, itemType, itemId, copies, zplPayload }) {
  const { data, error } = await supabase
    .from('print_jobs')
    .insert({
      branch_id: printer.branch_id,
      printer_id: printer.id,
      requested_by: userId,
      item_type: itemType,
      item_id: itemId,
      copies,
      zpl_payload: zplPayload,
      status: 'pending',
    })
    .select()
    .single();

  if (error) return { success: false, error: error.message };
  return { success: true, job: data };
}

// Only cancels while still pending — protects against racing a relay agent
// that already picked the job up.
export async function cancelPrintJob(jobId) {
  const { error } = await supabase
    .from('print_jobs')
    .update({ status: 'canceled' })
    .eq('id', jobId)
    .eq('status', 'pending');
  return { success: !error, error: error?.message };
}

// Same watch-one-job-live pattern as PrinterRegistryPage.js's "Send test
// label" — centralized here so every page that prints via Zebra reuses it
// instead of building its own version.
export function subscribeToPrintJob(jobId, onUpdate) {
  const channel = supabase
    .channel(`print_job_${jobId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'print_jobs', filter: `id=eq.${jobId}` },
      (payload) => {
        onUpdate(payload.new);
        if (['done', 'error', 'canceled'].includes(payload.new.status)) {
          supabase.removeChannel(channel);
        }
      }
    )
    .subscribe();
  return channel;
}

// Creates N print_jobs rows sharing one batch_id, so the whole batch can be
// tracked on a single realtime channel instead of N separate ones. jobs is
// [{ itemId, copies, zplPayload }, ...] — one entry per item already built
// by the caller (buildToolZpl/buildInventoryZpl per item, same as the
// single-item path).
export async function dispatchZebraBatch({ printer, userId, itemType, jobs }) {
  const batchId = crypto.randomUUID();
  const rows = jobs.map(j => ({
    branch_id: printer.branch_id,
    printer_id: printer.id,
    requested_by: userId,
    item_type: itemType,
    item_id: j.itemId,
    copies: j.copies,
    zpl_payload: j.zplPayload,
    status: 'pending',
    batch_id: batchId,
  }));

  const { data, error } = await supabase.from('print_jobs').insert(rows).select();
  if (error) return { success: false, error: error.message };
  return { success: true, batchId, jobs: data };
}

// Aggregate live status for a whole batch — one channel filtered on
// batch_id (the partial index makes this cheap) instead of N per-job
// channels. Calls onUpdate with the full current array of jobs on every
// change; caller derives done/sent/pending/error counts from that array
// rather than this function pre-computing a summary shape that might not
// fit every caller.
export function subscribeToPrintBatch(batchId, initialJobs, onUpdate) {
  const state = new Map(initialJobs.map(j => [j.id, j]));
  const channel = supabase
    .channel(`print_batch_${batchId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'print_jobs', filter: `batch_id=eq.${batchId}` },
      (payload) => {
        state.set(payload.new.id, payload.new);
        onUpdate(Array.from(state.values()));
      }
    )
    .subscribe();
  return channel;
}

// Guarded the same way single-job cancelPrintJob is — only flips rows still
// pending, so a job the relay already picked up isn't yanked out from under
// it. Rows already sent/done/error are left alone.
export async function cancelPrintBatch(batchId) {
  const { error } = await supabase
    .from('print_jobs')
    .update({ status: 'canceled' })
    .eq('batch_id', batchId)
    .eq('status', 'pending');
  return { success: !error, error: error?.message };
}

// Retries only the errored rows in a batch by flipping them back to
// pending — reuses the zpl_payload already stored rather than rebuilding
// and re-inserting, since nothing about the payload changed between
// attempts.
export async function retryPrintBatchErrors(batchId) {
  const { error } = await supabase
    .from('print_jobs')
    .update({ status: 'pending', error_message: null })
    .eq('batch_id', batchId)
    .eq('status', 'error');
  return { success: !error, error: error?.message };
}

// #321. formatId (not a pixel count) so the caller cannot pass a stale number.
async function generateQRDataURL(url, formatId = '2x1') {
  try {
    return await QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: qrPixelWidth(formatId),
      color: { dark: '#000000', light: '#ffffff' },
    });
  } catch (e) {
    console.error('QR generation failed:', e);
    return null;
  }
}

// Session 78a. Hands a PDF blob to a hidden same-document frame and prints it.
//
// This REPLACES printLabelDocument(), which wrote an HTML string into a frame
// and is deleted. The mechanism is kept because it was right; only the payload
// changed. Three properties it inherits, each one paid for by a real defect:
//
//   1. NO POP-UP - #339, Session 77. window.open() needs a per-origin
//      permission nobody explains, and Dallas in September would have hit that
//      wall with no one to explain it. A frame this document already owns needs
//      no permission, because nothing is being opened.
//
//   2. PAINT BEFORE DIALOG - #159. The native print dialog is modal to the tab,
//      so firing it before React has committed its last render freezes the
//      caller mid-update: the receive sheet sat on "Saving..." because the
//      dialog took the tab before "Stock received" had painted. Two animation
//      frames guarantee the caller's final render is on screen first.
//
//   3. TEARDOWN AFTER SPOOL. Removing the frame while the job is still
//      spooling cancels it, so removal is deferred rather than done on
//      afterprint alone - and afterprint does not fire everywhere, hence the
//      60s backstop.
//
// One thing it does NOT inherit: the 500ms fallback timer. That existed because
// document.write does not re-fire `load` on every engine. This frame gets a real
// `src`, so its load event is the real one.
//
// And one thing it adds: the blob URL is revoked on cleanup. The HTML path owned
// no resource to release; this one does, and a receiving session that prints a
// hundred labels would otherwise pin a hundred PDFs in memory. Revoking early
// does the same damage as tearing the frame down early - the viewer may not have
// finished reading - so it rides the same deferred cleanup.
//
// Returns true once the blob is handed to a frame. It deliberately does NOT
// report whether paper came out: nothing in the browser can tell us that, and a
// return value implying otherwise would be the dishonest claim Session 76
// removed from the destination label.
// #479, Speak Up OOB-6. Save the PDF instead of printing it.
//
// A SEPARATE CONTROL, not a variant of printPdfBlob, and Colby said why:
// "there should also be another way for people download and print them too
// probably." The print dialog is right for a printer on the desk and useless
// for sending a poster to a print shop, emailing it to a branch, or keeping a
// copy. Two different jobs, so two buttons rather than one that guesses.
//
// The deferred revoke is the same defensive detail printPdfBlob and savePdf
// both carry: Safari cancels the download if the object URL dies in the same
// frame the click happened in.
export function downloadPdfBlob(blob, filename = 'stede.pdf') {
  if (typeof document === 'undefined' || !document.body) return false;
  if (!blob || typeof URL === 'undefined' || !URL.createObjectURL) return false;

  let url;
  try {
    url = URL.createObjectURL(blob);
  } catch (e) {
    console.error('Blob URL creation failed:', e);
    return false;
  }

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.cssText = 'position:absolute;left:-9999px;top:0;';
  document.body.appendChild(a);
  a.click();
  try { document.body.removeChild(a); } catch { /* already detached */ }
  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
  }, 1000);
  return true;
}

export function printPdfBlob(blob, filename = 'stede-labels.pdf') {
  if (typeof document === 'undefined' || !document.body) return false;
  if (!blob || typeof URL === 'undefined' || !URL.createObjectURL) return false;

  let url;
  try {
    url = URL.createObjectURL(blob);
  } catch (e) {
    console.error('Blob URL creation failed:', e);
    return false;
  }

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', 'Stede label print');
  frame.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;border:0;';

  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    // Deferred past the dialog, exactly as above: tearing the frame down while
    // the job is still spooling cancels it. Revoking the URL early does the
    // same thing to a PDF the viewer has not finished reading.
    setTimeout(() => {
      try { frame.remove(); } catch { /* already detached */ }
      try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
    }, 1000);
  };

  // Session 78a. The label ALWAYS reaches the user, even if the dialog does not.
  //
  // Driving a print dialog from a frame containing a PDF is the one link in
  // this path that cannot be proven from a test or from this machine - it
  // depends on the browser's PDF viewer, and the machine with the label printer
  // is a different one. Without this, a browser that declines leaves the user
  // with NOTHING: no dialog, no file, no error. That is worse than the bug this
  // session is fixing, because at least a wrong label tells you something
  // happened.
  //
  // So a failure hands over the PDF instead. Downloads is a worse workflow than
  // a dialog and an infinitely better one than silence - and it is exactly what
  // Colby did by hand at Session 78a to get a label out of the machine at all.
  let salvaged = false;
  const salvage = () => {
    if (salvaged || done) return;
    salvaged = true;
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { try { a.remove(); } catch { /* already detached */ } }, 0);
    } catch (e) {
      console.error('PDF salvage download failed:', e);
    }
    cleanup();
  };

  // Set the instant before print() is called, never after: a dialog left open
  // for longer than the backstop must NOT trigger a download behind it, and
  // `done` cannot carry that because it is only set once printing finishes.
  let attempted = false;

  const fire = () => {
    const win = frame.contentWindow;
    if (!win) { salvage(); return; }
    try {
      win.addEventListener('afterprint', cleanup, { once: true });
      win.focus();
      attempted = true;
      win.print();
    } catch (e) {
      console.error('Hidden-frame PDF print failed, falling back to download:', e);
      salvage();
      return;
    }
    setTimeout(cleanup, 60000);
  };

  // #159: two frames, so the caller's final render has painted before the
  // modal dialog takes the tab.
  frame.addEventListener('load', () => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(fire));
    } else {
      setTimeout(fire, 0);
    }
  }, { once: true });

  // A blob URL that never renders fires no load event and no error event, so
  // neither branch above would ever run. 8s is long enough that a slow viewer
  // is not cut off and short enough that nobody concludes the button is dead.
  // Guarded on `attempted` rather than `done`, so a dialog the user leaves open
  // while they pick a printer does not quietly download a second copy behind it.
  setTimeout(() => { if (!attempted && !done && !salvaged) salvage(); }, 8000);

  frame.src = url;
  document.body.appendChild(frame);

  return true;
}

// Session 78a. The shared tail of all four browser-print entry points.
//
// Each one resolves its own records, QR codes and format, then hands the
// expanded page list here. Keeping the build-and-print pair in one place is
// what stops the four paths drifting apart again - #322 and Session 76 both
// found a bug that existed in some of the four and not the others, twice,
// because each carried its own copy of the geometry.
async function buildAndPrint(pages, branchName, formatId, subjectType, profileId = 'pt_e560bt_2x1') {
  // The page has to match the MEDIA, not the format's name. "2 x 1" is a label
  // SIZE; the thing it prints on is a continuous tape whose printable band is
  // narrower than both the tape and the nominal inch. Passing the format's
  // 1.00in through unchanged is what clipped the item name off Session 78a's
  // second test print. Every format the `local` destination offers is nominally
  // one inch tall, so this applies to all of them.
  const profile = LABEL_PROFILES[profileId] || LABEL_PROFILES.pt_e560bt_2x1;
  const mediaHeightIn = (profile.printable_mm || profile.height_mm) / 25.4;
  const { blob, error } = await buildLabelPdfBlob({ pages, branchName, formatId, subjectType, mediaHeightIn });
  if (!blob) return { ok: false, error: error || 'pdf_build_failed' };
  // Named for the salvage download, which is the only path where a person ever
  // sees it. `stede-2x1-labels.pdf` in Downloads says what it is; `download.pdf`
  // does not, and a receiving session can produce several in a row.
  const filename = `stede-${formatId}-labels.pdf`;
  if (!printPdfBlob(blob, filename)) return { ok: false, error: 'print_frame_failed' };
  return { ok: true, error: null };
}

export async function printInventoryLabel(item, branchName, copies = 1) {
  const formatId = PROFILE_FORMAT.pt_e560bt_2x1;
  const url = labelTargetUrl(`/inventory/scan/${item.id}`);
  const qrDataUrl = await generateQRDataURL(url, formatId);
  if (!qrDataUrl) return { success: false, error: 'QR generation failed' };

  const pages = Array(Math.max(1, copies)).fill({ record: item, qrDataUrl });
  const { ok, error } = await buildAndPrint(pages, branchName, formatId, 'inventory', 'pt_e560bt_2x1');
  if (!ok) return { success: false, error };

  return {
    success: true,
    copies,
    itemId: item.id,
    itemName: item.name,
    profileId: 'pt_e560bt_2x1',
    printedAt: new Date().toISOString(),
  };
}

export async function printToolLabel(tool, branchName, copies = 1, profileId = 'pt_e560bt_2x1') {
  const formatId = PROFILE_FORMAT[profileId] || '2x1';
  const url = labelTargetUrl(`/tool/${tool.id}`);
  const qrDataUrl = await generateQRDataURL(url, formatId);
  if (!qrDataUrl) return { success: false, error: 'QR generation failed' };

  // Session 78a: the two tool templates deleted with the rest of the HTML path
  // are not replaced by two PDF ones. They differed only in which fields they
  // showed and how big the QR was, and drawLabelTile derives both - the fields
  // from SUBJECT_TYPES, the QR from the format's short edge. Two templates
  // would be a second copy of a decision the registry already owns.
  const pages = Array(Math.max(1, copies)).fill({ record: tool, qrDataUrl });
  const { ok, error } = await buildAndPrint(pages, branchName, formatId, 'tool', profileId);
  if (!ok) return { success: false, error };

  return {
    success: true,
    copies,
    toolId: tool.id,
    toolName: tool.name,
    profileId,
    printedAt: new Date().toISOString(),
  };
}

// `formatId` added Session 76, defaulting to the 2x1 this hardcoded in TWO
// places - the QR size and the page size - regardless of what the caller chose.
export async function printInventoryBatch(items, branchName, formatId = '2x1') {
  if (!items || items.length === 0) return { success: false, error: 'no_items' };

  const pageGroups = await Promise.all(
    items.map(async ({ item, copies = 1 }) => {
      const url = labelTargetUrl(`/inventory/scan/${item.id}`);
      const qrDataUrl = await generateQRDataURL(url, formatId);
      // A record whose QR failed still gets its pages, carrying the QR-failure
      // box drawLabelTile draws. Dropping it silently is the failure mode
      // #113 was logged for: a batch that comes back short with nothing said.
      return Array(Math.max(1, copies)).fill({ record: item, qrDataUrl });
    })
  );

  const totalLabels = items.reduce((sum, { copies = 1 }) => sum + copies, 0);
  const pages = pageGroups.flat();
  const { ok, error } = await buildAndPrint(pages, branchName, formatId, 'inventory', 'pt_e560bt_2x1');
  if (!ok) return { success: false, error };

  return {
    success: true,
    totalLabels,
    itemCount: items.length,
    formatId,
    printedAt: new Date().toISOString(),
  };
}

// `copies` added Session 75 for gate decision D10 - batch copies as one stepper
// for the whole batch. `copiesById` added Session 76, when D10's per-item half
// was pulled forward: it overrides `copies` for the ids it names, and `copies`
// remains the fallback for every id it does not.
//
// A map rather than a { tool, copies } row shape deliberately - the row shape
// would make `tools` ambiguous between two entry types, and printInventoryBatch
// already owns that pattern. Here the argument stays a plain array of tools and
// the quantities travel beside it.
export async function printToolBatch(tools, branchName, profileId = 'pt_e560bt_2x1', copies = 1, copiesById = null) {
  if (!tools || tools.length === 0) return { success: false, error: 'no_tools' };

  const formatId = PROFILE_FORMAT[profileId] || '2x1';
  const copiesFor = tool => Math.max(1, copiesById?.[tool.id] ?? copies);

  const pageGroups = await Promise.all(
    tools.map(async (tool) => {
      const url = labelTargetUrl(`/tool/${tool.id}`);
      const qrDataUrl = await generateQRDataURL(url, formatId);
      return Array(copiesFor(tool)).fill({ record: tool, qrDataUrl });
    })
  );

  const { ok, error } = await buildAndPrint(pageGroups.flat(), branchName, formatId, 'tool', profileId);
  if (!ok) return { success: false, error };

  return {
    success: true,
    totalLabels: tools.reduce((sum, tool) => sum + copiesFor(tool), 0),
    profileId,
    printedAt: new Date().toISOString(),
  };
}

// #335, Session 79.22b.6.3.2 (Colby's R1 D1): getLastPrintJob() and
// saveLastPrintJob() are deleted. The save wrote a reprint FUNCTION through
// JSON.stringify, which drops functions, and nothing in the app ever read the
// store back - a feature that read as present and did nothing in either half.
// Reprinting, if it is ever wanted, is a row in #326's label export history.